import { CalendarIcon } from 'lucide-react';
import { useMemo } from 'react';
import { type Control, Controller, useFormState, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  calendarSelectionToParisMidnightUtcIso,
  formatCompactTokensLabel,
  formatQuotaDate,
  getTomorrowParisCalendarDate,
  isoToParisCalendarDate,
} from '../quota-format';

type QuotaConfigFieldsProps = {
  control: Control<Record<string, unknown>>;
  disabled?: boolean;
  namePrefix?: 'quota.' | '';
};

function fieldName(prefix: 'quota.' | '', key: 'tokenLimit' | 'renewalIntervalDays' | 'renewsAt' | 'enforcementEnabled') {
  return prefix ? `${prefix}${key}` : key;
}

export function QuotaConfigFields({
  control,
  disabled = false,
  namePrefix = '',
}: QuotaConfigFieldsProps) {
  const { errors } = useFormState({ control });
  const tokenLimit = useWatch({
    control,
    name: fieldName(namePrefix, 'tokenLimit'),
  });
  const renewsAt = useWatch({
    control,
    name: fieldName(namePrefix, 'renewsAt'),
  });
  const minSelectableDate = useMemo(() => getTomorrowParisCalendarDate(), []);
  const selectedDate = isoToParisCalendarDate(typeof renewsAt === 'string' ? renewsAt : undefined);
  const nestedErrors = namePrefix === 'quota.' ? errors.quota : errors;
  const tokenLimitError = nestedErrors && typeof nestedErrors === 'object' && 'tokenLimit' in nestedErrors
    ? (nestedErrors as { tokenLimit?: { message?: string } }).tokenLimit
    : undefined;

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Field>
        <FieldLabel htmlFor={`${namePrefix}tokenLimit`}>Limite de tokens</FieldLabel>
        <Input
          id={`${namePrefix}tokenLimit`}
          type="number"
          min={1}
          disabled={disabled}
          {...control.register(fieldName(namePrefix, 'tokenLimit'), { valueAsNumber: true })}
        />
        <FieldDescription>{formatCompactTokensLabel(typeof tokenLimit === 'number' ? tokenLimit : Number(tokenLimit))}</FieldDescription>
        <FieldError errors={[tokenLimitError]} />
      </Field>

      <Field>
        <FieldLabel htmlFor={`${namePrefix}renewalIntervalDays`}>Intervalle de renouvellement (jours)</FieldLabel>
        <Input
          id={`${namePrefix}renewalIntervalDays`}
          type="number"
          min={1}
          max={365}
          disabled={disabled}
          {...control.register(fieldName(namePrefix, 'renewalIntervalDays'), { valueAsNumber: true })}
        />
      </Field>

      <Field className="lg:col-span-2">
        <FieldLabel>Prochaine date de renouvellement</FieldLabel>
        <Controller
          control={control}
          name={fieldName(namePrefix, 'renewsAt')}
          render={({ field, fieldState }) => (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    className={cn(
                      'h-10 w-full justify-start border-input bg-background px-3 font-normal hover:bg-background',
                      !selectedDate && 'text-muted-foreground',
                    )}
                  >
                    <CalendarIcon className="mr-2 size-4" />
                    {selectedDate ? formatQuotaDate(typeof field.value === 'string' ? field.value : undefined) : 'Sélectionner une date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    disabled={{ before: minSelectableDate }}
                    onSelect={(date) => {
                      if (!date) {
                        return;
                      }

                      field.onChange(calendarSelectionToParisMidnightUtcIso(date));
                    }}
                  />
                </PopoverContent>
              </Popover>
              <FieldDescription>Le quota se renouvelle à partir du jour sélectionné (minuit, Europe/Paris).</FieldDescription>
              <FieldError errors={[fieldState.error]} />
            </>
          )}
        />
      </Field>

      <Field className="lg:col-span-2">
        <div className="flex items-center justify-between rounded-[var(--radius-surface-3xl)] border border-border bg-muted/40 px-4 py-3">
          <div className="space-y-1">
            <FieldLabel htmlFor={`${namePrefix}enforcementEnabled`}>Enforcement actif</FieldLabel>
            <FieldDescription>Bloque l’usage lorsque la limite est atteinte pour les comptes démo.</FieldDescription>
          </div>
          <Controller
            control={control}
            name={fieldName(namePrefix, 'enforcementEnabled')}
            render={({ field }) => (
              <Switch
                id={`${namePrefix}enforcementEnabled`}
                checked={Boolean(field.value)}
                disabled={disabled}
                onCheckedChange={field.onChange}
              />
            )}
          />
        </div>
      </Field>
    </div>
  );
}
